import * as Unwrappers from './unwrap';
import * as AdminUnwrappers from './unwrap_admin';
import { HttpUtils } from '../utils/http';

class BaseModel {

    constructor(data) {
        this.deleted = false;
        this._listeners = this._listeners || {};
        this.updateModel(data);
    }

    updateModel(data) {
        if (data) {
            this._data = data;
        }
        if (data) {
            // assign all core objects
            for (const n in data) {
                const m = data[n];
                if (typeof (m) !== 'function') {
                    this[n] = m;
                }
            }
        }
        if (this._data && this._data.$href) {
            this.self = this._data.$href('self');
            // append get functions for all links...
            // for exmaple if the embedded object contains a link to 'people' we create two functions:
            // getPeople()  - which resolves eventaully to an array of People (this may take a few digest loops). This is good for use in views
            // getPeoplePromise()  -  which always returns a promise of the object - which is useful in controllers
            const links = this.$links();
            this.__linkedData = {};
            this.__linkedPromises = {};
            (() => {
                const result = [];
                for (const link in links) {
                    const obj = links[link];
                    const name = this._snakeToCamel(`get_${link}`);
                    result.push(((link, obj, name) => {
                        if (!this[name]) {
                            this[name] = function (options) {
                                return this.$buildOject(link, options);
                            };
                        }
                        if (!this[`$${name}`]) {
                            return this[`$${name}`] = function (options) {
                                return this.$buildOjectPromise(link, options);
                            };
                        }
                    })(link, obj, name));
                }
                return result;
            })();
        }
        // call any listeners
        Object.values(this._listeners).map(func => func(this));
    }

    /**
     * Register a listener function that will be executed whenever a pusher update is received from the API.
     * @param identifier {string} A unique name used to identify the listener and avoid multiple registrations.
     * @param func {function} The function to execute.
     */
    registerListener(identifier, func) {
        if (!this._listeners[identifier]) {
            this._listeners[identifier] = func;
        }
    }

    /**
     * Remove a listener function from the model.
     * @param identifier {string} The unique name used to identify the listener function.
     */
    removeListener(identifier) {
        if (this._listeners[identifier]) {
            delete this._listeners[identifier];
        }
    }

    _snakeToCamel(s) {
        return s.replace(/(\_\w)/g, m => m[1].toUpperCase());
    }

    // build out a linked object
    $buildOject(link, options) {
        const linkId = link + (JSON.stringify(options) || '');

        if (this.__linkedData[linkId]) {
            return this.__linkedData[linkId];
        }
        this.$buildOjectPromise(link, options).then((ans) => {
                return this.__linkedData[linkId] = ans;
            }
        );
        return null;
    }

    // build a promise for a linked object
    $buildOjectPromise(link, options) {
        const linkId = link + (JSON.stringify(options) || '');

        if (this.__linkedPromises[linkId]) {
            return this.__linkedPromises[linkId];
        }

        this.__linkedPromises[linkId] = new Promise((resolve, reject) => {

            this.$get(link, options).then((res) => {
                    const inj = this.self.includes('/admin/') ? AdminUnwrappers[link] || Unwrappers[link] : Unwrappers[link];
                    if (inj) {
                        if (inj.promise) {
                            // unwrap involving another promise
                            return inj.unwrap(res).then(ans => resolve(ans)
                                , err => reject(err));
                        } else {
                            // unwrap without a promise
                            return resolve(inj.unwrap(res));
                        }
                    } else {
                        // no service found - just return the resources as I found it
                        return resolve(res);
                    }
                }
                , err => reject(err));

        });

        return this.__linkedPromises[linkId];
    }


    get(ikey) {
        if (!this._data) {
            return null;
        }
        return this._data[ikey];
    }

    set(ikey, value) {
        if (!this._data) {
            return null;
        }
        return this._data[ikey] = value;
    }

    getOption(ikey) {
        if (!this._data) {
            return null;
        }
        return this._data.getOption(ikey);
    }

    ignoreKeys() {
        return ['self'];
    }

    setOption(ikey, value) {
        if (!this._data) {
            return null;
        }
        return this._data.setOption(ikey, value);
    }

    jsonify(data = null) {
        return HttpUtils.jsonify(data || this, this.ignoreKeys());
    }

    halify(data = null) {
        data = data || this;
        const json = this.jsonify(data);
        if (this._data && this._data.halify) {
            this._data.halify(json);
        }
        return json;
    }

    $href(rel, params) {
        if (this._data) {
            return this._data.$href(rel, params);
        }
    }

    $has(rel) {
        if (this._data) {
            return this._data.$has(rel);
        }
    }

    $flush(rel, params, { all } = {}) {
        if (this._data) {
            return this._data.$flush(rel, params, all);
        }
    }

    $get(rel, params) {
        if (this._data) {
            return this._data.$get(rel, params);
        }
    }

    $getCollection(rel, params) {
        if (this._data) {
            return this._data.$getCollection(rel, params);
        }
    }

    $post(rel, params, dat) {
        if (this._data) {
            return this._data.$post(rel, params, dat);
        }
    }

    $put(rel, params, dat) {
        if (this._data) {
            return this._data.$put(rel, params, dat);
        }
    }

    $patch(rel, params, dat) {
        if (this._data) {
            return this._data.$patch(rel, params, dat);
        }
    }

    $del(rel, params, dat) {
        if (this._data) {
            return this._data.$del(rel, params, dat);
        }
    }

    $links() {
        if (this._data) {
            return this._data.$links();
        }
    }

    $link(rel) {
        if (this._data) {
            return this._data.$link(rel);
        }
    }

    $toStore() {
        if (this._data) {
            return this._data.$toStore();
        }
    }

    $update(data, options) {
        return this.$put('self', options, data || this.jsonify())
            .then((resource) => {
                return Promise.resolve(new this.constructor(resource));
            });
    }

    $delete() {
        if (this._data) {
            return this._data.$del('self', {});
        }
    }

    $refetch() {
        this.$flush('self');
        return this.$get('self')
            .then((resource) => {
                this.constructor(resource);
                return Promise.resolve(this);
            });
    }

    /**
     * @description
     * Assures that property is defined and fetched from the server.
     */
    async $assure(property) {
        this[property] = this[property] || await this['$get' + property[0].toUpperCase() + property.substring(1)]();
        return this[property];
    }
}

export {
    BaseModel
};
